/**
 * PDLC deliverable columns in the Products Spreadsheet (#809).
 *
 * A column is a virtual one: its value is a task in the product's Asana PDLC
 * project, read from the `pdlc_deliverables` snapshot, never a metadata field.
 * Asana does not link a project task back to its template task, so the task
 * NAME is the only join. Extend `taskNames` by hand when the template renames a
 * task — current template first, older-template aliases after.
 */
export const PDLC_DELIVERABLES = [
    { key: 'pdlcPcf', label: 'PCF', taskNames: ['Complete PCF SIGN OFF', 'PCF SIGN OFF'] },
    // Never match on "NPSO" alone: Gate 2's "Signature: Product Ready for NPSO" would hit.
    { key: 'pdlcNpso', label: 'NPSO', taskNames: ['Complete NPSO DOCUMENT SIGN OFF', 'NPSO DOCUMENT SIGN OFF'] },
    {
        key: 'pdlcColorBox',
        label: 'Color Box',
        taskNames: [
            'Create Retail Color Box (Retail Only)',
            'COLORBOX Design',
            'ColorBox Design (If DJ Product)',
            'Design Colorbox',
            'Create Color Box',
        ],
    },
    { key: 'pdlcPls', label: 'PLS', taskNames: ['PLS Approval / Sign-Off', 'PLS Approval / SIGN OFF', 'PLS Approval'] },
];
export const PDLC_DELIVERABLE_KEYS = PDLC_DELIVERABLES.map((d) => d.key);
/**
 * Shipment stage whose dates show beside a column (#880). Stage names are the
 * admin-editable `shipment.stage` list, so a renamed stage must be renamed here.
 * The column's done state stays the Asana sign-off: sample dates are an earlier
 * step and never feed it.
 */
export const PDLC_SHIPMENT_STAGES = {
    pdlcPls: 'PLS Sample',
};
const iso = (d) => (d ? new Date(d).toISOString() : undefined);
/**
 * A shipment's pipeline dates. Shipped is the first carrier scan (`in_transit`),
 * which is Ship24's `inTransitDatetime`; reading it from stored events means
 * existing shipments need no re-poll.
 */
export function pdlcShipmentDates(s) {
    const shipped = (s.events ?? [])
        .filter((e) => e.rawStatus === 'in_transit' && e.timestamp)
        .map((e) => new Date(e.timestamp).getTime())
        .sort((x, y) => x - y)[0];
    const out = {
        shippedAt: shipped === undefined ? undefined : new Date(shipped).toISOString(),
        eta: iso(s.estimatedDelivery),
        deliveredAt: iso(s.actualDelivery),
        receivedAt: iso(s.pdReceivedAt),
    };
    return Object.fromEntries(Object.entries(out).filter(([, v]) => v));
}
/**
 * Product statuses whose snapshot is kept current. Back Burner, Launched and
 * Discontinued keep whatever they last held.
 */
export const PDLC_LIVE_STATUSES = [
    '0 - Rework',
    '1 - Production',
    '2 - PLS',
    '3 - Development',
    '4 - Concept',
];
const norm = (s) => s.trim().replace(/\s+/g, ' ').toLowerCase();
/** Which task in `taskNames` order each deliverable resolves to. First match wins. */
export function matchPdlcDeliverables(tasks) {
    const byName = new Map();
    for (const t of tasks)
        if (!byName.has(norm(t.name)))
            byName.set(norm(t.name), t);
    const out = {};
    for (const d of PDLC_DELIVERABLES) {
        const hit = d.taskNames.map((n) => byName.get(norm(n))).find(Boolean);
        if (hit)
            out[d.key] = { taskGid: hit.gid, completed: hit.completed };
    }
    return out;
}
/** The deliverable a task gid backs in a row, if any. */
export function deliverableKeyForTask(deliverables, taskGid) {
    return PDLC_DELIVERABLE_KEYS.find((k) => deliverables[k]?.taskGid === taskGid);
}
//# sourceMappingURL=pdlc-deliverables.js.map